import resolveFrontendModule from "@app/resolveModule/frontend";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import { showPopover } from "@core-ui/showPopover";
import type { GesCloudRecoverPaymentInfo } from "@ext/enterprise-cloud/GesCloudApi";
import { getAcquiringReturnUrl } from "@ext/enterprise-cloud/utils/getAcquiringReturnUrl";
import t from "@ext/localization/locale/translate";
import { useCallback, useEffect, useState } from "react";
import DateUtils from "../../../../ui-logic/utils/dateUtils";
import {
	ButtonWithLoadingState,
	formatCurrencyWithSymbol,
	OfferAcceptanceNotice,
	PayModalLayout,
	PayModalSummary,
	PayModalSummarySeparator,
	PaymentInfoLoader,
	Row,
} from "./paymentModalComponents";
import { usePayModalState } from "./usePayModalState";

export interface RecoverPayModalProps {
	onClose: () => void;
	onSuccess?: () => void;
}

export const RecoverPayModal = ({ onClose, onSuccess }: RecoverPayModalProps) => {
	const { gesCloudApi, open, error, setError, idempotenceKey, onOpenChangeHandler } = usePayModalState(onClose);
	const [paymentInfo, setPaymentInfo] = useState<GesCloudRecoverPaymentInfo | null>(null);
	const [paying, setPaying] = useState(false);
	const { isTauri } = usePlatform();

	useEffect(() => {
		const loadPaymentInfo = async () => {
			try {
				const data = await gesCloudApi.getRecoverPaymentInfo();
				setPaymentInfo(data);
			} catch {
				setError(t("enterprise-cloud.org-settings.subscription.payment.load-error"));
			}
		};

		void loadPaymentInfo();
	}, [gesCloudApi, setError]);

	const onPay = useCallback(async () => {
		setPaying(true);
		setError(null);
		try {
			const result = await gesCloudApi.recoverFromSuspended(idempotenceKey, {
				returnUrl: getAcquiringReturnUrl(isTauri),
			});

			if (result.status === "redirect") {
				if (isTauri) {
					await resolveFrontendModule("gesCloudOpenAcquiring")(result.redirectUrl);
					onClose();
					return;
				}
				window.location.href = result.redirectUrl;
				return;
			}

			if (result.status === "ok") onSuccess?.();

			if (result.status === "pending")
				showPopover(t("enterprise-cloud.org-settings.subscription.payment.autopay-success"));

			onClose();
		} catch {
			setError(t("enterprise-cloud.org-settings.subscription.payment.pay-error"));
			setPaying(false);
		}
	}, [gesCloudApi, onClose, onSuccess, idempotenceKey, setError, isTauri]);

	const title = t("enterprise-cloud.org-settings.subscription.payment.recover-title");
	const isResetToFree = paymentInfo?.type === "resetToFree";
	const isYearly = paymentInfo?.type === "recoverPaid" && paymentInfo.billingPeriod === "year";
	const payNowAmount =
		paymentInfo?.type === "recoverPaid"
			? formatCurrencyWithSymbol(paymentInfo.payNowAmount, paymentInfo.currency)
			: "";
	const periodUnit = isYearly
		? t("enterprise-cloud.org-settings.subscription.payment.period-unit-year")
		: t("enterprise-cloud.org-settings.subscription.payment.period-unit-month");
	const periodicPayment = isYearly
		? t("enterprise-cloud.org-settings.subscription.payment.yearly")
		: t("enterprise-cloud.org-settings.subscription.payment.monthly");
	const nextPeriodStartDate = getNextPeriodStartDate(isYearly ? "year" : "month");
	const nextPeriodLabel = t("enterprise-cloud.org-settings.subscription.payment.periodic-payment-from")
		.replace("{periodicPayment}", periodicPayment)
		.replace("{date}", DateUtils.formatDate(nextPeriodStartDate, { dateStyle: "short" }));
	const payButtonLabel = isResetToFree
		? t("enterprise-cloud.org-settings.subscription.payment.recover-confirm")
		: t("enterprise-cloud.org-settings.subscription.payment.pay-amount").replace("{amount}", payNowAmount);

	return (
		<PayModalLayout
			description={
				paymentInfo && !isResetToFree
					? t("enterprise-cloud.org-settings.subscription.payment.recover-description")
					: undefined
			}
			error={error}
			onOpenChange={onOpenChangeHandler}
			open={open}
			title={title}
		>
			{paymentInfo ? (
				paymentInfo.type === "resetToFree" ? (
					<div className="text-sm">
						{t("enterprise-cloud.org-settings.subscription.payment.reset-to-free-message")}
					</div>
				) : (
					<PayModalSummary>
						<div className="flex flex-col gap-2">
							<Row
								accent="primary"
								label={t("enterprise-cloud.org-settings.subscription.payment.due-today-title")}
								value={payNowAmount}
							/>
							<Row
								accent="muted"
								label={t("enterprise-cloud.org-settings.subscription.payment.editor-seats-for-period")
									.replace("{editorsCount}", String(paymentInfo.editorsCount))
									.replace("{periodUnit}", periodUnit)}
							/>
						</div>

						<PayModalSummarySeparator />

						<div className="flex flex-col gap-2">
							<Row
								label={nextPeriodLabel}
								value={formatCurrencyWithSymbol(paymentInfo.paymentPerPeriod, paymentInfo.currency)}
							/>
							<Row
								accent="muted"
								label={`${paymentInfo.editorsCount} × ${formatCurrencyWithSymbol(
									paymentInfo.pricePerEditorPerPeriod,
									paymentInfo.currency,
								)}`}
							/>
						</div>
					</PayModalSummary>
				)
			) : (
				<PaymentInfoLoader />
			)}

			<ButtonWithLoadingState
				disabled={!paymentInfo || Boolean(error)}
				isLoading={paying}
				loadingText={title}
				onClick={onPay}
				text={payButtonLabel}
				variant="primary"
			/>
			{paymentInfo && !isResetToFree && <OfferAcceptanceNotice />}
		</PayModalLayout>
	);
};

const getNextPeriodStartDate = (billingPeriod: "month" | "year") => {
	const date = new Date();
	if (billingPeriod === "year") date.setFullYear(date.getFullYear() + 1);
	else date.setMonth(date.getMonth() + 1);
	return date;
};
